
import React, { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import FormInput from "@/components/auth/common/FormInput";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useNursingHomesByCounty } from "@/hooks/use-nursing-homes-by-county";
import { useJobApplication, jobApplicationSchema, JobApplicationFormData } from "@/hooks/use-job-application";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";

interface JobApplicationFormProps {
  counties: { county: string; county_slug: string }[];
}

export function JobApplicationForm({ counties }: JobApplicationFormProps) {
  const [selectedCountySlug, setSelectedCountySlug] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { submitJobApplication, isSubmitting, submissionSuccess } = useJobApplication();
  
  const form = useForm<JobApplicationFormData>({
    resolver: zodResolver(jobApplicationSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      preferredCounty: "",
      nursingHomeSlug: "",
      hasExperience: "no",
      legalRightToWork: undefined,
      message: "",
      consent: false,
    },
  });

  // Load nursing homes based on selected county
  const { data: nursingHomes, isLoading: nursingHomesLoading } = useNursingHomesByCounty(
    selectedCountySlug ? [selectedCountySlug] : null
  );

  const handleCountyChange = (value: string) => {
    form.setValue("preferredCounty", value);
    setSelectedCountySlug(value);
    // Reset nursing home selection when county changes
    form.setValue("nursingHomeSlug", "");
  };

  const onSubmit = async (data: JobApplicationFormData) => {
    const success = await submitJobApplication(data);
    if (success) {
      form.reset();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  if (submissionSuccess) {
    return (
      <Alert className="bg-green-50 border-green-200 my-8">
        <CheckCircle className="h-5 w-5 text-green-500" />
        <AlertTitle className="text-green-800 text-lg font-medium">Application Submitted Successfully!</AlertTitle>
        <AlertDescription className="text-green-700">
          <p className="mb-4">Thank you for your interest in joining our team. We have received your application and will review it shortly.</p>
          <p>Our recruitment team will contact you by email or phone if your experience and qualifications match our requirements.</p>
          <Button 
            onClick={() => window.location.reload()} 
            variant="outline" 
            className="mt-4 border-green-300 text-green-700 hover:bg-green-100"
          >
            Submit Another Application
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormInput
          control={form.control}
          name="fullName"
          label="Full Name"
          required
        />

        <FormInput
          control={form.control}
          name="email"
          label="Email Address"
          required
        />

        <FormInput
          control={form.control}
          name="phone"
          label="Phone Number"
          required
        />

        <div className="space-y-3">
          <Label required>Preferred County</Label>
          <Select 
            onValueChange={handleCountyChange}
            value={form.watch("preferredCounty")}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a county" />
            </SelectTrigger>
            <SelectContent>
              {counties.map((county) => (
                <SelectItem key={county.county_slug} value={county.county_slug}>
                  {county.county}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.formState.errors.preferredCounty && (
            <p className="text-destructive text-sm">
              {form.formState.errors.preferredCounty.message}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <Label>Specific Nursing Home (Optional)</Label>
          <Select 
            onValueChange={(value) => form.setValue("nursingHomeSlug", value)}
            value={form.watch("nursingHomeSlug") || ""}
            disabled={!selectedCountySlug || nursingHomesLoading}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a nursing home (optional)" />
            </SelectTrigger>
            <SelectContent>
              {nursingHomesLoading ? (
                <SelectItem value="loading" disabled>Loading...</SelectItem>
              ) : nursingHomes && nursingHomes.length > 0 ? (
                nursingHomes.map((home) => (
                  <SelectItem key={home.nh_slug} value={home.nh_slug}>
                    {home.center_name}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="none" disabled>No nursing homes available</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          <Label>Do you have experience in elderly care?</Label>
          <RadioGroup
            onValueChange={(value) => form.setValue("hasExperience", value as "yes" | "no")}
            defaultValue="no"
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="yes" />
              <Label htmlFor="yes">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="no" />
              <Label htmlFor="no">No</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-3">
          <Label required>Do you have the legal right to work in Ireland?</Label>
          <RadioGroup
            onValueChange={(value) => form.setValue("legalRightToWork", value as "yes" | "no")}
            value={form.watch("legalRightToWork") || ""}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="legal-right-yes" />
              <Label htmlFor="legal-right-yes">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="legal-right-no" />
              <Label htmlFor="legal-right-no">No</Label>
            </div>
          </RadioGroup>
          {form.formState.errors.legalRightToWork && (
            <p className="text-destructive text-sm mt-1">
              {String(form.formState.errors.legalRightToWork.message)}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <Label required>Cover Letter / Message</Label>
          <Textarea
            placeholder="Please tell us a bit about yourself and why you're interested in this position..."
            className="min-h-[120px]"
            {...form.register("message")}
          />
          {form.formState.errors.message && (
            <p className="text-destructive text-sm">
              {String(form.formState.errors.message.message)}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <Label required>Upload your CV</Label>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            ref={fileInputRef}
            onChange={(e) => {
              form.setValue("cvFile", e.target.files);
              form.trigger("cvFile");
            }}
            className="w-full cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm file:mr-4 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-sm file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
          />
          {form.formState.errors.cvFile && (
            <p className="text-destructive text-sm">
              {String(form.formState.errors.cvFile.message)}
            </p>
          )}
        </div>

        <div className="flex items-start space-x-2">
          <Checkbox 
            id="consent" 
            checked={form.watch("consent")}
            onCheckedChange={(checked) => {
              form.setValue("consent", checked === true);
              form.trigger("consent");
            }}
          />
          <div className="grid gap-1.5 leading-none">
            <label
              htmlFor="consent"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I agree to the privacy policy and consent to having my personal data processed.
            </label>
            {form.formState.errors.consent && (
              <p className="text-destructive text-sm">
                {String(form.formState.errors.consent.message)}
              </p>
            )}
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            "Submit Application"
          )}
        </Button>

        <p className="text-sm text-gray-500 mt-4">
          By submitting this form, you agree to our{" "}
          <a href="/legal/privacy-policy" className="text-purple-600 hover:underline">
            Privacy Policy
          </a>
          . We'll only use your information to process your job application.
        </p>
      </form>
    </Form>
  );
}
